import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  X,
  Receipt,
  Upload,
  ArrowLeftRight,
  Download,
  Percent,
  User,
  Clock,
  Loader2,
} from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatQuoterAmount } from "../utils/formatQuoterAmount.utils";
import { shippingQuotesService } from "../services/shippingQuotes.service";
import type { ShippingQuote } from "../types/shipping-quotes-api.types";

type QuoterHistoryDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string | null;
};

export const QuoterHistoryDetailModal = ({
  isOpen,
  onClose,
  quoteId,
}: QuoterHistoryDetailModalProps) => {
  const { t } = useTranslations("quoter");
  const [quote, setQuote] = useState<ShippingQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuote = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    setQuote(null);
    try {
      const data = await shippingQuotesService.getById(quoteId);
      setQuote(data);
    } catch {
      setError(t("history_modal_error_load"));
    } finally {
      setLoading(false);
    }
  }, [quoteId, t]);

  useEffect(() => {
    if (isOpen && quoteId) {
      fetchQuote();
    }
  }, [isOpen, quoteId, fetchQuote]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className="w-full max-w-lg transform overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-xl flex items-center justify-center text-teal-600 dark:text-teal-400">
                      <Receipt className="size-5" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                      {t("history_modal_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                    aria-label={t("history_modal_close_aria")}
                  >
                    <X className="size-5" />
                  </button>
                </header>

                <div className="px-6">
                  {loading && (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="size-8 animate-spin text-teal-600 dark:text-teal-400" />
                    </div>
                  )}

                  {error && !loading && (
                    <p className="py-8 text-sm text-red-600 dark:text-red-400">
                      {error}
                    </p>
                  )}

                  {quote && !loading && (
                    <>
                      <div className="px-8 py-8 text-center bg-slate-50/50 dark:bg-slate-800/30">
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">
                          {t("history_modal_amount_receive")}
                        </p>
                        <h1 className="text-4xl font-extrabold text-teal-600 dark:text-teal-400 flex items-center justify-center gap-2 flex-wrap">
                          {formatQuoterAmount(quote.amount_to_receive)}{" "}
                          <span className="text-2xl font-semibold opacity-80">
                            {quote.destination_currency}
                          </span>
                        </h1>
                      </div>

                      <div className="px-8 py-8 space-y-0">
                        <div className="relative flex gap-4 pb-8">
                          <div className="absolute left-[11px] top-6 w-[2px] bg-slate-200 dark:bg-slate-700 h-[72px]" />
                          <div className="shrink-0 z-10 w-6 h-6 rounded-full bg-teal-600 dark:bg-teal-500 flex items-center justify-center ring-4 ring-white dark:ring-slate-900 ml-0.5">
                            <Upload className="text-white size-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                              {t("history_modal_origin")}
                            </p>
                            <div className="flex justify-between items-baseline mt-0.5 gap-2">
                              <span className="text-lg font-bold text-slate-700 dark:text-slate-200 truncate">
                                {formatQuoterAmount(quote.amount_to_send)} {quote.origin_currency}
                              </span>
                              <span className="text-sm text-slate-500 font-medium shrink-0">
                                {quote.country_origin ?? "—"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="relative ml-10 py-4 -mt-2">
                          <div className="inline-flex items-center gap-3 bg-teal-500/5 dark:bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 ring-4 ring-white dark:ring-slate-900 relative z-10">
                            <ArrowLeftRight className="size-5 text-teal-600 dark:text-teal-400 shrink-0" />
                            <span className="text-sm font-bold text-teal-600 dark:text-teal-400">
                              {quote.exchange_rate ?? "—"}
                            </span>
                          </div>
                        </div>

                        <div className="relative flex gap-4 pt-4 pb-8">
                          <div className="shrink-0 z-10 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center ring-4 ring-white dark:ring-slate-900">
                            <Download className="text-white size-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                              {t("history_modal_destination")}
                            </p>
                            <div className="flex justify-between items-baseline mt-0.5 gap-2">
                              <span className="text-lg font-bold text-slate-700 dark:text-slate-200 truncate">
                                {formatQuoterAmount(quote.amount_to_receive)}{" "}
                                {quote.destination_currency}
                              </span>
                              <span className="text-sm text-slate-500 font-medium shrink-0">
                                {quote.country_destination ?? "—"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="ml-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-800 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                              <Percent className="size-4" />
                              {t("history_modal_commission", { percent: quote.commission })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-slate-200/50 dark:border-slate-700/50">
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                              {t("history_modal_total_debit")}
                            </span>
                            <span className="text-sm font-bold text-slate-800 dark:text-white">
                              {formatQuoterAmount(quote.amount_to_send)} {quote.origin_currency}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="px-8 py-4 border-t border-slate-100 dark:border-slate-800 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                          <User className="size-3.5 shrink-0" />
                          <span>
                            {t("history_modal_created_by")}{" "}
                            <span className="text-slate-600 dark:text-slate-300 font-medium">
                              {quote.created_by_user?.email ??
                                quote.created_by_user?.full_name ??
                                "—"}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                          <Clock className="size-3.5 shrink-0" />
                          <span>
                            Fecha:{" "}
                            <span className="text-slate-600 dark:text-slate-300 font-medium">
                              {formatDateTime(quote.created_at, {
                                includeTime: true,
                              })}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="px-8 pb-8 pt-4">
                        <button
                          type="button"
                          onClick={onClose}
                          className="w-full py-3.5 px-6 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold rounded-xl transition-all"
                        >
                          {t("history_modal_close_button")}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
